import { useState, type ReactElement } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'app/components/ui/dialog'
import { Button } from 'app/components/ui/button'
import { Input } from 'app/components/ui/input'
import { Field, FieldDescription, FieldLabel } from 'app/components/ui/field'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'app/components/ui/select'
import ComboMultiSelect from 'app/components/ui/combo/combo-multi-select'
import type { OptionType } from 'types/OptionType'
import { useOpsEvalsData } from './OpsEvalsContext'
import { useAvailableModels } from './useAvailableModels'
import { SINGLE_VERSIONS, STAGE_VERSIONS } from './mockData'
import { estimateRunCost, type CostEstimate } from './estimate'
import { useTokenCounter } from './tokenizer'
import { formatCost, formatTokens } from './pricing'
import {
  PIPELINE_STAGES,
  type ExecutionMode,
  type Model,
  type PipelineStage,
  type PromptSet,
  type RunConfig,
} from './types'
import type { NewRunPrefill } from './useEvalsPanel'

function VersionSelect({
  value,
  options,
  onChange,
}: {
  value: number
  options: number[]
  onChange: (value: number) => void
}): ReactElement {
  return (
    <Select value={String(value)} onValueChange={(next) => onChange(Number(next))}>
      <SelectTrigger className="h-8 w-20 text-sm">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((version) => (
          <SelectItem key={version} value={String(version)}>
            v{version}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

const STAGED = `staged`

/** Which pipeline a side runs: production's 5 stages, or a version of the single prompt. */
function PipelineSelect({
  value,
  disabled,
  onChange,
}: {
  value: PromptSet
  disabled: boolean
  onChange: (single: number | undefined) => void
}): ReactElement {
  return (
    <Select
      value={value.single === undefined ? STAGED : String(value.single)}
      disabled={disabled}
      onValueChange={(next) => onChange(next === STAGED ? undefined : Number(next))}
    >
      <SelectTrigger className="h-8 w-28 text-sm">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={STAGED}>5 stages</SelectItem>
        {SINGLE_VERSIONS.map((version) => (
          <SelectItem key={version} value={String(version)}>
            Single v{version}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

// The Select trigger is the reference control. The model combobox (h-12 by default) and
// the number input (an inset surface by default) are brought to the same height, radius,
// border and fill so the fields read as one form. `.combobox-button` is unlayered CSS,
// which beats layered utilities, so its box properties need the important modifier.
const CONTROL_CLASS = `h-9 rounded-md border-input bg-transparent px-3 text-sm`

function CostEstimateBox({
  estimate,
  tokenizerReady,
}: {
  estimate: CostEstimate
  tokenizerReady: boolean
}): ReactElement {
  const totalTokens = estimate.promptTokens + estimate.completionTokens
  return (
    <div className="rounded-lg border border-default bg-surface-subtle px-4 py-3">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-sm font-medium text-strong">Estimated cost</div>
          <div className="mt-0.5 text-caption-sm text-subtle">
            {estimate.items} {estimate.items === 1 ? `item` : `items`} × {estimate.models}
            {` `}
            {estimate.models === 1 ? `model` : `models`} · ~{formatTokens(totalTokens)} tokens · candidate + baseline
          </div>
        </div>
        <div className="text-xl font-semibold tabular-nums text-strong">{formatCost(estimate.totalCost)}</div>
      </div>
      {estimate.perModel.length > 1 && (
        <div className="mt-3 space-y-1 border-t border-subtle pt-3">
          {estimate.perModel.map((model) => (
            <div key={model.model} className="flex justify-between text-caption-sm">
              <span className="text-subtle">{model.model}</span>
              <span className={model.unpriced ? `text-minimal` : `tabular-nums text-default`}>
                {model.unpriced ? `no pricing` : formatCost(model.cost)}
              </span>
            </div>
          ))}
        </div>
      )}
      {estimate.hasUnpriced && (
        <p className="mt-2 text-caption-sm text-amber-700">
          Some selected models have no pricing yet, so they add $0 to the total.
        </p>
      )}
      <p className="mt-2 text-caption-sm text-minimal">
        {tokenizerReady
          ? `Input tokens counted with gpt-tokenizer, including message and schema overhead. Output tokens are estimated.`
          : `Approximate while the tokenizer loads. Output tokens are estimated.`}
      </p>
    </div>
  )
}

export function NewRunWizard({
  prefill,
  running,
  onClose,
  onRun,
}: {
  prefill: NewRunPrefill
  running: boolean
  onClose: () => void
  onRun: (config: RunConfig) => void
}): ReactElement {
  const { datasets, itemsFor, runError } = useOpsEvalsData()
  const { models: availableModels, loading: modelsLoading } = useAvailableModels()
  const [datasetId, setDatasetId] = useState(prefill.datasetId ?? datasets[0]?.id ?? ``)
  const [candidate, setCandidate] = useState<PromptSet>(prefill.candidate)
  const [baseline, setBaseline] = useState<PromptSet>(prefill.baseline)
  const [models, setModels] = useState<Model[]>([`gpt-5-mini`])
  const [temperature, setTemperature] = useState(0)
  const [execution, setExecution] = useState<ExecutionMode>(`simulated`)
  const isLocal = execution !== `simulated`
  const peopleOnly = execution === `local-people`
  const runStages: readonly PipelineStage[] = peopleOnly ? [`people`] : PIPELINE_STAGES
  // The People-stage mode runs one stage prompt, so both sides use the stages.
  const sides = peopleOnly
    ? { candidate: { ...candidate, single: undefined }, baseline: { ...baseline, single: undefined } }
    : { candidate, baseline }

  const setVersion = (setter: typeof setCandidate, stage: PipelineStage, value: number) =>
    setter((prev) => ({ ...prev, [stage]: value }))

  const modelOptions: OptionType[] = availableModels.map((model) => ({ name: model, id: model, value: model }))
  const modelValues: OptionType[] = models.map((model) => ({ name: model, id: model, value: model }))

  const { count, ready: tokenizerReady } = useTokenCounter()
  const items = itemsFor(datasetId)
  const estimate = estimateRunCost({ items, models, ...sides, countTokens: count, stages: runStages })

  const canRun = datasetId && models.length > 0 && !running

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New eval run</DialogTitle>
          <DialogDescription>
            Run a candidate prompt set against a golden dataset across one or more models.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-x-8 gap-y-5 md:grid-cols-2">
          {/* Left column — dataset, execution, models, temperature */}
          <div className="flex flex-col gap-5">
            <Field>
              <FieldLabel>Dataset</FieldLabel>
              <Select value={datasetId} onValueChange={setDatasetId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select dataset" />
                </SelectTrigger>
                <SelectContent>
                  {datasets.map((dataset) => (
                    <SelectItem key={dataset.id} value={dataset.id}>
                      {dataset.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            {__OPS_EVAL_RUNNER__ && (
              <Field>
                <FieldLabel>Execution</FieldLabel>
                <Select value={execution} onValueChange={(next) => setExecution(next as ExecutionMode)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="simulated">Simulated (synthetic scores)</SelectItem>
                    <SelectItem value="local-pipeline">Real model, full pipeline (dev only)</SelectItem>
                    <SelectItem value="local-people">Real model, People stage (dev only)</SelectItem>
                  </SelectContent>
                </Select>
                {isLocal && (
                  <FieldDescription>
                    {peopleOnly
                      ? `Calls OpenAI from this dev server with OPENAI_API_KEY. Only the People stage runs and is scored.`
                      : `Calls OpenAI from this dev server with OPENAI_API_KEY: once per stage, or once for a single prompt, then stage 5's rules.`}
                  </FieldDescription>
                )}
              </Field>
            )}

            <Field>
              <FieldLabel>Models</FieldLabel>
              <ComboMultiSelect
                name="models"
                className="w-full"
                inputClassName={`${CONTROL_CLASS} !h-9 !rounded-md !px-3 !py-0 [&_.truncate]:mt-0`}
                options={modelOptions}
                values={modelValues}
                loading={modelsLoading}
                placeholder="Search models…"
                onChange={(options) => setModels(options.map((option) => String(option.value)))}
              />
            </Field>

            <Field>
              <FieldLabel>Temperature</FieldLabel>
              <Input
                type="number"
                min={0}
                max={2}
                step={0.1}
                className={CONTROL_CLASS}
                value={temperature}
                disabled={isLocal}
                onChange={(e) => setTemperature(Number(e.target.value))}
              />
              {isLocal && (
                <FieldDescription>Not used: production runs each model at its default temperature.</FieldDescription>
              )}
            </Field>
          </div>

          {/* Right column — prompt versions */}
          <Field>
            <FieldLabel>Prompt versions</FieldLabel>
            <div className="overflow-hidden rounded-lg border border-default">
              <div className="grid grid-cols-[1fr_7rem_7rem] items-center gap-3 border-b border-default bg-surface-subtle px-3 py-2 text-caption-sm font-medium text-subtle">
                <span>Stage</span>
                <span className="text-center">Candidate</span>
                <span className="text-center">Baseline</span>
              </div>
              <div className="grid grid-cols-[1fr_7rem_7rem] items-center gap-3 border-b border-default px-3 py-1.5">
                <span
                  className={`text-sm ${sides.candidate.single !== sides.baseline.single ? `font-medium text-blue-600` : `text-default`}`}
                >
                  Pipeline
                </span>
                <PipelineSelect
                  value={sides.candidate}
                  disabled={peopleOnly}
                  onChange={(single) => setCandidate((prev) => ({ ...prev, single }))}
                />
                <PipelineSelect
                  value={sides.baseline}
                  disabled={peopleOnly}
                  onChange={(single) => setBaseline((prev) => ({ ...prev, single }))}
                />
              </div>
              {PIPELINE_STAGES.map((stage) => {
                const staged = [sides.candidate, sides.baseline].filter((side) => side.single === undefined)
                const changed = staged.length === 2 && candidate[stage] !== baseline[stage]
                const runs = runStages.includes(stage) && staged.length > 0
                return (
                  <div
                    key={stage}
                    className={`grid grid-cols-[1fr_7rem_7rem] items-center gap-3 border-b border-subtle px-3 py-1.5 last:border-b-0 ${runs ? `` : `opacity-50`}`}
                  >
                    <span className={`text-sm capitalize ${changed ? `font-medium text-blue-600` : `text-default`}`}>
                      {stage}
                    </span>
                    {[
                      { side: sides.candidate, setter: setCandidate },
                      { side: sides.baseline, setter: setBaseline },
                    ].map(({ side, setter }, index) =>
                      side.single === undefined ? (
                        <VersionSelect
                          key={index}
                          value={side[stage]}
                          options={STAGE_VERSIONS[stage]}
                          onChange={(value) => setVersion(setter, stage, value)}
                        />
                      ) : (
                        <span key={index} className="text-center text-caption-sm text-minimal">
                          not used
                        </span>
                      ),
                    )}
                  </div>
                )
              })}
            </div>
            <FieldDescription>
              {peopleOnly
                ? `Only the People stage runs in this mode. Changed rows are highlighted.`
                : `Summary and action items are produced but not scored. Changed rows are highlighted.`}
            </FieldDescription>
          </Field>
        </div>

        <CostEstimateBox estimate={estimate} tokenizerReady={tokenizerReady} />

        {runError && <p className="text-sm text-red-600">Run failed: {runError}</p>}

        <DialogFooter>
          <Button variant="tertiary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            loading={running}
            disabled={!canRun}
            onClick={() => onRun({ datasetId, ...sides, models, temperature, execution })}
          >
            Run eval
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
